import { useFormik } from "formik";
import { Button } from "primereact/button";

import { VACATION_POLICY_STRINGS } from "../constants/strings";
import { validationSchema } from "../validationSchema";
import type { UpdateVacationPolicyFormValuesT } from "../domain/types/vacationPolicy.types";
import type { VacationUiT } from "../domain/ui/vacationPolicy.model";

import { Heading, InputNumber, InputRadio } from "components/v2";
import Label from "components/v2/Label";
import LeaveIconsPicker from "screens/Vacations/components/LeaveIconsPicker/LeaveIconsPicker";

type VacationPolicyFormPropsT = {
  data?: VacationUiT;
  hasFetchingDataError?: boolean;
  isUpdating?: boolean;
  onSubmitForm: (values: UpdateVacationPolicyFormValuesT) => Promise<void>;
  dataTestId?: string;
};

const VacationPolicyForm = ({
  data,
  hasFetchingDataError,
  isUpdating,
  onSubmitForm,
  dataTestId,
}: VacationPolicyFormPropsT) => {
  const initialValues = {
    icon: data?.type?.icon ?? "airplane-takeoff",
    seniorityRequirement:
      data?.policy?.minimumEligibilityDays === 365
        ? "one_year"
        : "from_first_day",
    minimumEligibilityDays:
      data?.policy?.minimumEligibilityDays === 365
        ? 90
        : (data?.policy?.minimumEligibilityDays ?? 90),
    accrualMode: data?.policy?.accrualMode ?? "PROPORTIONAL_ACCRUAL",
    allowNegativeBalance: data?.policy?.allowNegativeBalance ? "yes" : "no",
    advanceNoticeRequirement: data?.policy?.minAdvanceNoticeDays
      ? "required"
      : "none",
    minAdvanceNoticeDays: data?.policy?.minAdvanceNoticeDays || 5,
    allowBackdateDays: data?.policy?.maxBackdateDays ? "yes" : "no",
    maxBackdateDays: data?.policy?.maxBackdateDays || 5,
  };

  const { values, errors, handleChange, handleSubmit, setFieldValue } =
    useFormik({
      initialValues,
      validationSchema,
      enableReinitialize: true,
      validateOnChange: true,
      onSubmit: (formValues) =>
        onSubmitForm(
          validationSchema.cast(formValues) as UpdateVacationPolicyFormValuesT,
        ),
    });

  const formHasErrors = errors && Object.keys(errors).length > 0;

  const renderMinSeniorityRequirement = () => {
    return (
      <div className="flex flex-col gap-1" data-testid="minimum-eligibility-days-wrapper">
        <InputRadio
          label={VACATION_POLICY_STRINGS.LABEL_SENIORITY_REQUIREMENT}
          checkedValue={values.seniorityRequirement}
          name="seniorityRequirement"
          onChange={handleChange}
          error={errors?.seniorityRequirement}
          radios={[
            {
              id: "one-year",
              label: VACATION_POLICY_STRINGS.RADIO_SENIORITY_ONE_YEAR,
              value: "one_year",
            },
            {
              id: "from-first-day",
              label: VACATION_POLICY_STRINGS.RADIO_SENIORITY_FROM_FIRST_DAY,
              value: "from_first_day",
            },
          ]}
          isRequired
        />
        {values.seniorityRequirement === "from_first_day" && (
          <InputNumber
            id="minimum-eligibility-days"
            name="minimumEligibilityDays"
            label={VACATION_POLICY_STRINGS.INPUT_SENIORITY_DAYS_LABEL}
            placeholder={
              VACATION_POLICY_STRINGS.INPUT_SENIORITY_DAYS_PLACEHOLDER
            }
            suffixText={VACATION_POLICY_STRINGS.DAYS_TEXT}
            value={values.minimumEligibilityDays}
            onChange={handleChange}
            error={errors?.minimumEligibilityDays}
            className="!w-56"
            helperText={VACATION_POLICY_STRINGS.INPUT_SENIORITY_DAYS_HELPER}
            isRequired
          />
        )}
      </div>
    );
  };

  const renderAccrualMode = () => {
    return (
      <InputRadio
        label={VACATION_POLICY_STRINGS.LABEL_ACCRUAL_METHOD}
        checkedValue={values.accrualMode}
        name="accrualMode"
        onChange={handleChange}
        error={errors?.accrualMode}
        inline={false}
        radios={[
          {
            id: "earned-days-only",
            label: VACATION_POLICY_STRINGS.RADIO_ACCRUAL_ANNUAL_GRANTED,
            value: "ANNUAL_GRANTED",
          },
          {
            id: "accrued-proportionally",
            label: VACATION_POLICY_STRINGS.RADIO_ACCRUAL_PROPORTIONAL,
            value: "PROPORTIONAL_ACCRUAL",
          },
        ]}
        isRequired
      />
    );
  };

  const renderAllowNegativeBalance = () => {
    return (
      <InputRadio
        label={VACATION_POLICY_STRINGS.LABEL_ADVANCE_LEAVE}
        checkedValue={values.allowNegativeBalance}
        name="allowNegativeBalance"
        onChange={handleChange}
        error={errors?.allowNegativeBalance}
        radios={[
          {
            id: "allow-advance-leave-yes",
            label: VACATION_POLICY_STRINGS.RADIO_ADVANCE_LEAVE_YES,
            value: "yes",
          },
          {
            id: "allow-advance-leave-no",
            label: VACATION_POLICY_STRINGS.RADIO_ADVANCE_LEAVE_NO,
            value: "no",
          },
        ]}
        isRequired
      />
    );
  };

  const renderAdvanceNoticeRequirement = () => {
    return (
      <div className="flex flex-col gap-1" data-testid="allow-advance-vacation-wrapper">
        <InputRadio
          label={VACATION_POLICY_STRINGS.LABEL_ADVANCE_NOTICE_REQUIREMENT}
          checkedValue={values.advanceNoticeRequirement}
          name="advanceNoticeRequirement"
          onChange={handleChange}
          error={errors?.advanceNoticeRequirement}
          radios={[
            {
              id: "none",
              label: VACATION_POLICY_STRINGS.RADIO_ADVANCE_NOTICE_NONE,
              value: "none",
            },
            {
              id: "required",
              label: VACATION_POLICY_STRINGS.RADIO_ADVANCE_NOTICE_REQUIRED,
              value: "required",
            },
          ]}
          isRequired
        />
        {values.advanceNoticeRequirement === "required" && (
          <InputNumber
            id="min-advance-notice-days"
            name="minAdvanceNoticeDays"
            label={VACATION_POLICY_STRINGS.INPUT_ADVANCE_NOTICE_LABEL}
            placeholder={
              VACATION_POLICY_STRINGS.INPUT_ADVANCE_NOTICE_PLACEHOLDER
            }
            suffixText={VACATION_POLICY_STRINGS.DAYS_TEXT}
            value={values.minAdvanceNoticeDays}
            onChange={handleChange}
            error={errors?.minAdvanceNoticeDays}
            className="!w-56"
            helperText={VACATION_POLICY_STRINGS.INPUT_ADVANCE_NOTICE_HELPER}
            isRequired
          />
        )}
      </div>
    );
  };

  const renderAllowBackdateDays = () => {
    return (
      <div className="flex flex-col gap-1" data-testid="allow-backdated-requests-wrapper">
        <InputRadio
          label={VACATION_POLICY_STRINGS.LABEL_ALLOW_PAST_DATES}
          checkedValue={values.allowBackdateDays}
          name="allowBackdateDays"
          onChange={handleChange}
          error={errors?.allowBackdateDays}
          radios={[
            {
              id: "allow-past-dates-no",
              label: VACATION_POLICY_STRINGS.RADIO_PAST_DATES_NO,
              value: "no",
            },
            {
              id: "allow-past-dates-yes",
              label: VACATION_POLICY_STRINGS.RADIO_PAST_DATES_YES,
              value: "yes",
            },
          ]}
          isRequired
        />
        {values.allowBackdateDays === "yes" && (
          <InputNumber
            id="max-backdate-days"
            name="maxBackdateDays"
            label={VACATION_POLICY_STRINGS.INPUT_PAST_DATES_LABEL}
            placeholder={VACATION_POLICY_STRINGS.INPUT_PAST_DATES_PLACEHOLDER}
            suffixText={VACATION_POLICY_STRINGS.DAYS_TEXT}
            value={values.maxBackdateDays}
            onChange={handleChange}
            error={errors?.maxBackdateDays}
            className="!w-56"
            helperText={VACATION_POLICY_STRINGS.INPUT_PAST_DATES_HELPER}
            isRequired
          />
        )}
      </div>
    );
  };

  const handleOnIconClick = (iconName: string) => setFieldValue("icon", iconName);

  return (
    <div
      className="flex !w-full !h-full flex-col relative bg-white rounded-lg shadow-lg"
      data-testid={dataTestId}
    >
      <div className="flex w-full h-full relative">
        <div className="absolute inset-0 flex flex-col gap-5 overflow-auto p-5 xl:flex-row 2xl:flex-row">
          <form className="flex flex-col gap-4 xl:flex-1 2xl:flex-1">
            {renderMinSeniorityRequirement()}
            {renderAccrualMode()}
            {renderAllowNegativeBalance()}
            <Heading level={2} className="mt-2">
              {VACATION_POLICY_STRINGS.HEADER_DEADLINE_TO_REQUEST}
            </Heading>
            {renderAdvanceNoticeRequirement()}
            {renderAllowBackdateDays()}
          </form>
          <div className="flex h-auto flex-col gap-1 xl:flex-1 2xl:flex-1">
            <Label
              text={VACATION_POLICY_STRINGS.SELECT_ICON_LABEL}
              isRequired
            />
            <LeaveIconsPicker
              selectedIcon={values.icon}
              onIconClick={handleOnIconClick}
            />
          </div>
        </div>
      </div>
      <div className="flex w-full justify-end p-2">
        <Button
          className="'flex !w-64 !h-6 bg-blue-800 text-white'"
          label={VACATION_POLICY_STRINGS.BUTTON_UPDATE_LABEL}
          disabled={formHasErrors || hasFetchingDataError}
          onClick={handleSubmit}
          data-testid="update-vacation-policy-button"
          loading={isUpdating}
        />
      </div>
    </div>
  );
};

export default VacationPolicyForm;
